import { useEffect, useState } from 'react'
import { InputText } from 'primereact/inputtext'
import IconButton from '../../buttons/IconButton'
import { SaveIcon, PlusIcon, CopyIcon, EditIcon } from '../../Icons'
import { Dropdown } from 'primereact/dropdown'
import {
  BasicNameValue,
  MiscellaneousParameterList,
  StarCCMParameterList
} from '../../../types/types'
import toast from 'react-hot-toast'
import { useGetMiscParameters, useGetStarCCMParameters } from '../../../api/runconfig-API'
import Modal from '../../Modal'
import ParameterPresetChildInModal from './ParameterPresetChildInModal'
import { cloneMiscellaneousParameterList, cloneStarCCMParameterList } from '../../../types/util'

export const PresetTypeRegular = 'StarCCMParameterList'
export const PresetTypeMisc = 'MiscellaneousParameterList'

type ParameterPresetsProps = {
  title: string
  presetType: 'StarCCMParameterList' | 'MiscellaneousParameterList'
  currentPreset: StarCCMParameterList | MiscellaneousParameterList
  presets: StarCCMParameterList[] | MiscellaneousParameterList[]
  readOnly?: boolean
  allowLocalParameterValueEdit?: boolean
  updateCurrentSelectedPreset: (preset: StarCCMParameterList | MiscellaneousParameterList) => void
  saveSelectedPreset?: (
    preset: StarCCMParameterList | MiscellaneousParameterList
  ) => Promise<StarCCMParameterList | undefined> | Promise<MiscellaneousParameterList | undefined>
}

const ParameterPresetManagerModal: React.FC<ParameterPresetsProps> = ({
  title,
  presets,
  presetType,
  currentPreset,
  readOnly = false,
  allowLocalParameterValueEdit = false,
  saveSelectedPreset,
  updateCurrentSelectedPreset
}) => {
  const [, fetchStarccmParams] = useGetStarCCMParameters()
  const [, fetchMiscParams] = useGetMiscParameters()

  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [editMode, setEditMode] = useState<string>('') // 'new' or 'edit'

  const [selectedPreset, setSelectedPreset] = useState<
    StarCCMParameterList | MiscellaneousParameterList
  >()
  const [availablePresets, setAvailablePresets] = useState<
    StarCCMParameterList[] | MiscellaneousParameterList[]
  >([])
  const [availableParams, setAvailableParams] = useState<string[]>([])
  const [presetName, setPresetName] = useState<string>('')
  const [parameterChildren, setParameterChildren] = useState<BasicNameValue[]>([])

  useEffect(() => {
    setAvailablePresets(presets)
  }, [presets])

  useEffect(() => {
    if (isEditing && parameterChildren.length === 0) {
      setParameterChildren(prev => [
        ...prev,
        new BasicNameValue({
          name: '',
          value: ''
        })
      ])
    }
  }, [isEditing, parameterChildren?.length])

  // Fetch StarCCM parameter list
  useEffect(() => {
    const fetchStarccmParamsList = async () => {
      const starccmParamsList = []
      try {
        const starccmRes = await fetchStarccmParams()
        starccmRes.data.forEach(item => {
          starccmParamsList.push(item)
        })
      } catch {}
      setAvailableParams(prev => Array.from(new Set([...starccmParamsList, ...prev])))
    }

    const fetchMiscParamsList = async () => {
      const miscList = []
      try {
        const miscRes = await fetchMiscParams()
        miscRes.data.forEach(item => {
          miscList.push(item)
        })
      } catch {}
      setAvailableParams(prev => Array.from(new Set([...miscList, ...prev])))
    }

    if (isEditing) {
      if (presetType === PresetTypeRegular) {
        fetchStarccmParamsList()
      } else if (presetType === PresetTypeMisc) {
        fetchMiscParamsList()
      }
    }
  }, [isEditing])

  useEffect(() => {
    if (currentPreset) {
      setSelectedPreset(currentPreset)
      setParameterChildren(currentPreset.paramList || [])
      setPresetName(currentPreset.name || '')
    }
  }, [currentPreset])

  useEffect(() => {}, [parameterChildren])

  const handleAddChild = (newChild: BasicNameValue) => {
    setParameterChildren(prev => [...prev, newChild])
  }

  const handleDeleteChild = (index: number) => {
    setParameterChildren(prev => {
      const filtered = prev.filter((_, i) => i !== index)
      return filtered
    })
    setSelectedPreset(prev => {
      if (!prev) return prev
      const updatedPreset = Object.assign(Object.create(Object.getPrototypeOf(prev)), {
        ...prev,
        paramList: prev.paramList?.filter((_, i) => i !== index) || []
      })
      return updatedPreset
    })
  }

  const createPresetWithParameters = (
    sourcePreset: StarCCMParameterList | MiscellaneousParameterList | undefined,
    params: BasicNameValue[]
  ) => {
    const paramList = params.map(
      param => new BasicNameValue({ name: param.name, value: param.value })
    )
    // Clone through the message getters so protobuf metadata (_id, raceteamId, timestamps, isPreset) survives
    const preset =
      presetType === PresetTypeRegular
        ? sourcePreset
          ? cloneStarCCMParameterList(sourcePreset as StarCCMParameterList)
          : new StarCCMParameterList()
        : sourcePreset
        ? cloneMiscellaneousParameterList(sourcePreset as MiscellaneousParameterList)
        : new MiscellaneousParameterList()
    preset.name = sourcePreset?.name || presetName || ''
    preset.paramList = paramList
    return preset
  }

  const updateSelectedPresetLocally = (params: BasicNameValue[]) => {
    const updatedPreset = createPresetWithParameters(selectedPreset || currentPreset, params)
    setSelectedPreset(updatedPreset)
    updateCurrentSelectedPreset(updatedPreset)
  }

  // Adds a new parameter set to the current preset
  const handleAddParameter = (parameterName: string, index: number, value?: string) => {
    // Always add to availableParams if not present
    setAvailableParams(prev => {
      if (!parameterName || prev.includes(parameterName)) return prev
      toast.success(`Added Parameter: ${parameterName}`)
      return [...prev, parameterName]
    })
    setParameterChildren(prev => {
      const updated = prev.map(
        param => new BasicNameValue({ name: param.name, value: param.value })
      )
      if (!updated[index]) {
        return prev
      }
      if (parameterName) {
        updated[index].name = parameterName
      }
      if (value !== undefined) {
        updated[index].value = value
      }
      if (!isEditing && allowLocalParameterValueEdit) {
        updateSelectedPresetLocally(updated)
      }
      return updated
    })
  }

  const handleDuplicatePreset = () => {
    if (!selectedPreset?._id) {
      return
    }
    const dupParamList =
      selectedPreset?.paramList.map(
        param => new BasicNameValue({ name: param.name, value: param.value })
      ) || []
    const dupValues = {
      name: `${selectedPreset.name}_copy`,
      isPreset: selectedPreset.isPreset,
      paramList: dupParamList
    }
    const newPreset =
      currentPreset instanceof StarCCMParameterList
        ? new StarCCMParameterList(dupValues)
        : new MiscellaneousParameterList(dupValues)
    setIsEditing(true)
    setEditMode('new')
    setPresetName(newPreset.name || '')
    setParameterChildren(newPreset.paramList || [])
    setSelectedPreset(newPreset)
  }

  const handleEditPreset = () => {
    if (!selectedPreset?._id) {
      return
    }
    setIsEditing(true)
    setEditMode('edit')
    setPresetName(selectedPreset.name || '')
    setParameterChildren(selectedPreset.paramList || [])
  }

  const handleAddNewPreset = () => {
    setIsEditing(true)
    setEditMode('new')
    const now = new Date()
    const pad = (n: number) => n.toString().padStart(2, '0')
    const suffix = `${pad(now.getDate())}${pad(now.getMonth() + 1)}${now
      .getFullYear()
      .toString()
      .slice(-2)}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
    setPresetName(`NewPreset_${suffix}`)
    setParameterChildren([new BasicNameValue({ name: '', value: '' })])
  }

  const handleSavePreset = async () => {
    if (!presetName || !presetName.trim()) {
      toast.error('Preset name is required!')
      return
    }

    if (editMode === 'edit') {
      if (presets.some(p => p.name === presetName && p._id !== selectedPreset?._id)) {
        toast.error('Preset name already exists!')
        return
      }
    } else if (editMode === 'new') {
      if (presets.some(p => p.name === presetName)) {
        toast.error('Preset name already exists!')
        return
      }
    }

    // Create the new preset object
    const newPreset =
      presetType === PresetTypeRegular
        ? new StarCCMParameterList({ name: presetName })
        : new MiscellaneousParameterList({ name: presetName })
    if (editMode === 'edit' && selectedPreset?._id) {
      newPreset._id = selectedPreset._id // retain the same ID if editing
    }
    newPreset.paramList = parameterChildren
      .map(param => new BasicNameValue({ name: param.name, value: param.value }))
      .filter(param => param.name.trim() !== '' && param.value.trim() !== '')

    const savedPreset = await saveSelectedPreset?.(newPreset)
    if (savedPreset?._id) {
      newPreset._id = savedPreset._id
    }

    setSelectedPreset(newPreset)
    updateCurrentSelectedPreset(newPreset)
    setParameterChildren(newPreset.paramList || [])
    // Refresh the list of available presets
    setAvailablePresets(prev => {
      if (editMode === 'edit') {
        return prev.map(p => (p._id === newPreset._id ? newPreset : p))
      } else {
        return [...prev, newPreset]
      }
    })
    setIsEditing(false)
  }

  return (
    <>
      {/* Selector */}
      {!isEditing && (
        <>
          <div className="w-full grid grid-cols-9 gap-4">
            <div className="col-span-8">
              <Dropdown
                pt={{
                  root: {
                    className:
                      'min-w-[100%] bg-transparent dark:border-neutral-70 dark:bg-transparent'
                  },
                  input: {
                    className: 'py-2'
                  }
                }}
                disabled={readOnly}
                value={selectedPreset?._id || null}
                options={availablePresets?.map(
                  (p: StarCCMParameterList | MiscellaneousParameterList) => ({
                    label: p.name,
                    value: p._id,
                    preset: p
                  })
                )}
                onChange={e => {
                  const selected = availablePresets.find(
                    (p: StarCCMParameterList | MiscellaneousParameterList) => p._id === e.value
                  )
                  setSelectedPreset(selected)
                  setParameterChildren(selected?.paramList || [])
                  updateCurrentSelectedPreset(selected)
                }}
                placeholder={selectedPreset?.name || 'Select a preset'}
                emptyMessage="No presets available"
                emptyFilterMessage="No matching presets found"
                data-pr-tooltip="Select a preset to view"
                showClear
                filter
                filterPlaceholder="Search presets"
                panelClassName="min-w-full"
              />
            </div>
            {!readOnly && (
              <div className="col-span-1 flex items-center space-x-2">
                <IconButton
                  title="Create New Preset"
                  severity="info"
                  icon={<PlusIcon className="w-5 h-5 fill-white inline cursor-pointer" />}
                  onClick={handleAddNewPreset}
                />
                {selectedPreset?._id && (
                  <>
                    <IconButton
                      title="Duplicate Preset"
                      severity="secondary"
                      icon={<CopyIcon className="w-5 h-5 fill-white inline cursor-pointer" />}
                      onClick={() => handleDuplicatePreset()}
                    />
                    <IconButton
                      title="Edit Preset"
                      severity="secondary"
                      icon={<EditIcon className="w-5 h-5 fill-white inline cursor-pointer" />}
                      onClick={() => handleEditPreset()}
                    />
                  </>
                )}
              </div>
            )}
          </div>
          {/* Preset Population */}
          <div className="mt-2 w-full grid grid-cols-9 gap-2">
            {parameterChildren.map((p, idx) => (
              <ParameterPresetChildInModal
                key={`param-child-${idx}`}
                index={idx}
                localAvailableParams={availableParams}
                currentParameter={p}
                onAdd={handleAddChild}
                onAddLocalParameter={handleAddParameter}
                onDelete={() => handleDeleteChild(idx)}
                canAdd={isEditing && idx === parameterChildren.length - 1}
                canEdit={isEditing}
                canEditValue={allowLocalParameterValueEdit}
                canDelete={parameterChildren.length > 1}
              />
            ))}
          </div>
        </>
      )}
      {isEditing && (
        <Modal
          isOpen={true}
          withAction={false}
          easyClose={true}
          onCancel={() => {
            //onCancel && onCancel()
            setIsEditing(false)
            // Revert the state back based on the arguments passed
            setSelectedPreset(currentPreset)
            setPresetName(currentPreset?.name || '')
            setParameterChildren(currentPreset?.paramList || [])
          }}
          title={title}
          showCancelButton={true}
          zIndex={30}
          size="md">
          <div className="flex flex-col items-stretch min-w-[100%] max-w-[100%] space-y-4 mt-4">
            <div className="grid grid-cols-9 gap-2 max-h-[75vh] overflow-y-auto flex-grow">
              <InputText
                value={presetName}
                onChange={e => setPresetName(e.target.value)}
                placeholder="New preset name"
                className="col-span-8"
              />
              <div className="col-span-1"></div>
              {/* Preset Population */}
              {parameterChildren.map((p, idx) => (
                <ParameterPresetChildInModal
                  key={`param-child-${idx}`}
                  index={idx}
                  localAvailableParams={availableParams}
                  currentParameter={p}
                  onAdd={handleAddChild}
                  onAddLocalParameter={handleAddParameter}
                  onDelete={() => handleDeleteChild(idx)}
                  canAdd={idx === parameterChildren.length - 1}
                  canEdit={isEditing}
                  canDelete={parameterChildren.length > 1}
                />
              ))}
            </div>
            <div className="w-[100%] flex items-center justify-end basis-10">
              <IconButton
                title="Save Configuration"
                label="Save"
                additionalClassName=" !w-fit items-center !bg-gm-blue !p-2 text-sm"
                icon={<SaveIcon className="flex self-center w-4 h-4 fill-white" />}
                onClick={() => handleSavePreset()}
              />
            </div>
          </div>
        </Modal>
      )}
    </>
  )
}

export default ParameterPresetManagerModal
